import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  CounterpartiesListResponseSchema,
  CounterpartyResponseSchema,
  DeleteCounterpartyResponseSchema,
  type CounterpartiesListResponse,
  type CounterpartyResponse,
  type UpsertCounterpartyInput,
} from '../../shared/counterparty.ts';

export const COUNTERPARTIES_QUERY_KEY = ['counterparties'] as const;

export function counterpartiesListQueryKey(search = '') {
  return [...COUNTERPARTIES_QUERY_KEY, 'list', search.trim()] as const;
}

export function counterpartyDetailQueryKey(id: string) {
  return [...COUNTERPARTIES_QUERY_KEY, 'detail', id] as const;
}

export interface UpdateCounterpartyInput {
  id: string;
  counterparty: UpsertCounterpartyInput;
}

export function useCounterparties(search = ''): UseQueryResult<CounterpartiesListResponse, Error> {
  const normalizedSearch = search.trim();
  return useQuery<CounterpartiesListResponse, Error>({
    queryKey: counterpartiesListQueryKey(normalizedSearch),
    queryFn: () => fetchCounterparties(normalizedSearch),
    retry: false,
  });
}

export function useCounterparty(id: string): UseQueryResult<CounterpartyResponse, Error> {
  return useQuery<CounterpartyResponse, Error>({
    queryKey: counterpartyDetailQueryKey(id),
    queryFn: () => fetchCounterparty(id),
    retry: false,
    enabled: id.trim() !== '',
  });
}

export function useCreateCounterparty(): UseMutationResult<
  CounterpartyResponse,
  Error,
  UpsertCounterpartyInput
> {
  const queryClient = useQueryClient();
  return useMutation<CounterpartyResponse, Error, UpsertCounterpartyInput>({
    mutationFn: createCounterparty,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: COUNTERPARTIES_QUERY_KEY });
    },
  });
}

export function useUpdateCounterparty(): UseMutationResult<
  CounterpartyResponse,
  Error,
  UpdateCounterpartyInput
> {
  const queryClient = useQueryClient();
  return useMutation<CounterpartyResponse, Error, UpdateCounterpartyInput>({
    mutationFn: updateCounterparty,
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: COUNTERPARTIES_QUERY_KEY });
      await queryClient.invalidateQueries({
        queryKey: counterpartyDetailQueryKey(data.counterparty.id),
      });
    },
  });
}

export function useDeleteCounterparty(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: deleteCounterparty,
    onSuccess: async (_data, id) => {
      await queryClient.invalidateQueries({ queryKey: COUNTERPARTIES_QUERY_KEY });
      queryClient.removeQueries({ queryKey: counterpartyDetailQueryKey(id) });
    },
  });
}

async function fetchCounterparties(search: string): Promise<CounterpartiesListResponse> {
  const path = search
    ? `/api/counterparties?q=${encodeURIComponent(search)}`
    : '/api/counterparties';
  const response = await fetch(path, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(
      await readApiError(response, `GET /api/counterparties failed with status ${response.status}`),
    );
  }

  const payload: unknown = await response.json();
  return CounterpartiesListResponseSchema.parse(payload);
}

async function fetchCounterparty(id: string): Promise<CounterpartyResponse> {
  const path = `/api/counterparties/${encodeURIComponent(id)}`;
  const response = await fetch(path, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(
      await readApiError(response, `GET ${path} failed with status ${response.status}`),
    );
  }

  const payload: unknown = await response.json();
  return CounterpartyResponseSchema.parse(payload);
}

async function createCounterparty(input: UpsertCounterpartyInput): Promise<CounterpartyResponse> {
  const response = await fetch('/api/counterparties', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    body: JSON.stringify(toUpsertPayload(input)),
  });

  if (!response.ok) {
    throw new Error(
      await readApiError(
        response,
        `POST /api/counterparties failed with status ${response.status}`,
      ),
    );
  }

  const payload: unknown = await response.json();
  return CounterpartyResponseSchema.parse(payload);
}

async function updateCounterparty(input: UpdateCounterpartyInput): Promise<CounterpartyResponse> {
  const path = `/api/counterparties/${encodeURIComponent(input.id)}`;
  const response = await fetch(path, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    body: JSON.stringify(toUpsertPayload(input.counterparty)),
  });

  if (!response.ok) {
    throw new Error(
      await readApiError(response, `PUT ${path} failed with status ${response.status}`),
    );
  }

  const payload: unknown = await response.json();
  return CounterpartyResponseSchema.parse(payload);
}

async function deleteCounterparty(id: string): Promise<void> {
  const path = `/api/counterparties/${encodeURIComponent(id)}`;
  const response = await fetch(path, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(
      await readApiError(response, `DELETE ${path} failed with status ${response.status}`),
    );
  }

  const payload: unknown = await response.json();
  DeleteCounterpartyResponseSchema.parse(payload);
}

function toUpsertPayload(input: UpsertCounterpartyInput): UpsertCounterpartyInput {
  return {
    name: input.name,
    email: input.email,
    billTo: input.billTo,
    shipTo: input.shipTo,
  };
}

async function readApiError(response: Response, fallback: string): Promise<string> {
  try {
    const payload: unknown = await response.json();
    if (isApiError(payload)) {
      return payload.error;
    }
  } catch {
    // Keep the method/path/status fallback for non-JSON responses.
  }
  return fallback;
}

function isApiError(value: unknown): value is { error: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    typeof (value as { error?: unknown }).error === 'string'
  );
}
